import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import { useAuditAction } from '../../hooks/useAuditAction';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ConnectOnDesktopSheet } from '../shared/ConnectOnDesktopSheet';
import { SourceIcon } from '../shared/SourceIcon';
import { QuietDayState } from '../brief/QuietDayState';
import { RegenerateButton } from '../brief/RegenerateButton';
import { siteLabel } from '../../lib/sites';
import type {
  TeamTodaySurfaceData,
  BriefingCategory,
  NeedsInputItem,
  Diffstat,
  BranchDivergence,
  SharedDecision,
  ProjectProgress,
  StanceLean,
  TeammateStance,
} from '../../hooks/useTeamTodaySurface';

/*
 * Daily briefing — 1:1 reproduction of the locked mockup (mockups/dashboard-redesign.html
 * §s-briefing). Markup + classes are the mock's verbatim (.wrap/.greeting/.sub/.statstrip/
 * .sec-block/.sec-label/.mlist/.mrow/.icard/.cv-row/.section-note); all of those are already
 * ported into overhaul.css. Content is bound to the live BriefingSections contract — only the
 * design is copied from the mock, never its sample text.
 *
 * Contract gaps vs. the mock (bound to the closest real field, or omitted — NOT faked):
 *  - needsYourInput has no per-author avatars or timestamp → render tag/severity/sharedEntities.
 *  - relevantToYourWork has no source/timestamp → render authorName + variant.
 *  - knowledgeCaptured has no source logo or timestamp → render id + text + entity chips.
 *  - Resolve opens the conversation. Dismiss is durable: it hides the card optimistically AND
 *    persists a reject_relevance audit (needsYourInput + relevantToYourWork carry briefingItemId),
 *    so the today route's filterActionedItems drops it on reload (bug-315). Legacy briefings without
 *    a briefingItemId degrade to local-only hide.
 */

const AV_COLORS = ['blue', 'sage', 'sand', 'lilac', 'clay', 'teal'] as const;

function hashIndex(s: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % mod;
}

function avColor(name: string): string {
  return AV_COLORS[hashIndex(name || '?', AV_COLORS.length)];
}

function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function firstName(name?: string): string {
  return (name || '').trim().split(/\s+/).filter(Boolean)[0] || '';
}

function timeOfDay(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

function formatCrumb(iso: string | null): string {
  if (!iso) return 'DELIVER';
  const d = new Date(iso);
  const day = d
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    .replace(',', '') // mock crumb has no comma: "TUE JUN 23"
    .toUpperCase();
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `DELIVER · ${day} · ${time}`;
}

function relativeTime(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Stat-strip dot color from the contract's free-form `tone`. */
function statDot(tone: string): string {
  switch ((tone || '').toLowerCase()) {
    case 'grn':
    case 'green':
    case 'positive':
    case 'success':
      return 'grn';
    case 'amb':
    case 'amber':
    case 'warning':
      return 'amb';
    case 'rose':
    case 'alert':
    case 'danger':
    case 'attention':
      return 'rose';
    case 'cyan':
    case 'info':
      return 'cyan';
    default:
      return 'acc';
  }
}

/** What-changed leading dot color by category (mock: shipped=grn, still_building=acc, discussed=amb). */
function catDot(cat: BriefingCategory): string {
  switch (cat) {
    case 'shipped':
      return 'grn';
    case 'still_building':
      return 'acc';
    case 'discussed':
      return 'amb';
  }
}

function catLabel(cat: BriefingCategory): string {
  switch (cat) {
    case 'shipped':
      return 'Shipped';
    case 'still_building':
      return 'Still building';
    case 'discussed':
      return 'Discussed';
  }
}

function severityLabel(severity: NonNullable<NeedsInputItem['severity']>): string {
  switch (severity) {
    case 'collision':
      return 'strong overlap';
    case 'related':
      return 'related work';
    case 'coarse':
      return 'possible overlap';
  }
}

/** A short mono id (e.g. cf-3a1 / cv-3f02) derived from the conversation id. */
function shortId(prefix: string, conversationId: string): string {
  const slug = (conversationId || '').replace(/[^a-z0-9]/gi, '').slice(-4) || '----';
  return `${prefix}-${slug}`;
}

const ROSE_BORDER = 'color-mix(in srgb, var(--rose) 38%, transparent)';
const ROSE_LAB_BORDER = 'color-mix(in srgb, var(--rose) 40%, transparent)';

// figure b: maps a teammate's lean to a 0–100% position on the meter (poleFor=left, poleAgainst=right).
// no_signal has no position — callers filter it out (the backend already excludes it; this is defensive).
const LEAN_PERCENT: Record<Exclude<StanceLean, 'no_signal'>, number> = {
  strongly_for: 4,
  leaning_for: 28,
  neutral_undecided: 50,
  leaning_against: 72,
  strongly_against: 96,
};

/* figure c (data-backed): the "branch → base · +X −Y · N files" diffstat row, rendered only when a
 * whatChanged item carries GitHub-enriched diffstat. The add/remove segment bar is normalized to a
 * fixed width so a huge diff doesn't blow out the row. Styled in the overhaul vocabulary. */
function DiffstatRow({ diffstat }: { diffstat: Diffstat }) {
  const adds = diffstat.additions ?? 0;
  const dels = diffstat.deletions ?? 0;
  const files = diffstat.filesChanged ?? 0;
  const total = adds + dels;
  const SEGS = 10;
  const addSegs = total > 0 ? Math.round((adds / total) * SEGS) : 0;
  const delSegs = total > 0 ? SEGS - addSegs : 0;
  const branch = diffstat.branchMerged
    ? `${diffstat.branchMerged}${diffstat.baseBranch ? ` → ${diffstat.baseBranch}` : ''}`
    : null;
  return (
    <div className="diffstat">
      {branch && <span className="df-branch">{branch}</span>}
      <span className="df-add">+{adds}</span>
      <span className="df-del">−{dels}</span>
      <span>{files} file{files === 1 ? '' : 's'}</span>
      <span className="df-segs" aria-hidden>
        {Array.from({ length: addSegs }).map((_, i) => <i className="a" key={`a${i}`} />)}
        {Array.from({ length: delSegs }).map((_, i) => <i className="r" key={`r${i}`} />)}
      </span>
    </div>
  );
}

/* figure a (data-backed): real branch divergence + conflict, rendered only when a collision item
 * carries GitHub-enriched branchDivergence. The conflicting FILE is the only real part — GitHub REST
 * cannot expose conflict-line content, so the three line fields (lineNumber/yourLine/theirLine) were
 * deleted from the contract (phase-B walk D15) and the branches that read them are gone with them.
 * Nothing user-visible moves: the enricher set all three to null unconditionally, so the "+ mine /
 * − theirs" rows never rendered on a real briefing and the marker below is what always showed. */
function BranchDivergenceDetail({ div }: { div: BranchDivergence }) {
  const hunk = div.conflictHunk;
  const yourCap = `${div.branchYours ?? 'your branch'}${div.aheadBy != null ? ` ↑${div.aheadBy}` : ''}`;
  const theirCap = `${div.branchTheirs ?? 'base'}${div.behindBy != null ? ` ↓${div.behindBy}` : ''}${div.mergeableState ? ` · ${div.mergeableState}` : ''}`;
  return (
    <div className="branchdiv">
      <div className="bd-caps">
        <span className="bd-yours">{yourCap}</span>
        <span className="bd-base">{theirCap}</span>
      </div>
      {hunk && (
        <div className="bd-hunk">
          <div className="bd-file">{hunk.filePath}</div>
          <div className="bd-mut">conflict — resolve in {hunk.filePath}</div>
        </div>
      )}
    </div>
  );
}

function EmptyShell({ date, lede, children }: { date: string; lede: string; children?: ReactNode }) {
  return (
    <div className="v2-content ov-surface">
      <div className="wrap">
        <div className="crumb">DELIVER</div>
        <h1 className="greeting">{date}</h1>
        <p className="sub">{lede}</p>
        {children && <div style={{ marginTop: 22 }}>{children}</div>}
      </div>
    </div>
  );
}

// The "mark viewed" POST mutates TODAY's row, so it must NEVER fire in readOnly
// (historical) mode, and only for an unviewed, ready briefing. Extracted as a pure
// predicate so the readOnly gate is unit-testable without running React effects.
export function shouldMarkViewed(
  readOnly: boolean,
  status: TeamTodaySurfaceData['status'],
  viewedAt: string | null,
): boolean {
  return !readOnly && status === 'ready' && viewedAt === null;
}

export function TeamTodaySurface({
  data,
  onNav,
  userName,
  readOnly = false,
}: {
  data: TeamTodaySurfaceData;
  onNav: (id: string) => void;
  userName?: string;
  // When true, render the briefing as a read-only historical snapshot: no "mark
  // viewed" POST (that endpoint mutates TODAY's row). Used by the briefing-history
  // detail view, which reuses this renderer. The masthead's formatCrumb already
  // shows the briefing's own date, so a past briefing reads correctly as-is.
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const viewedRef = useRef(false);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const audit = useAuditAction();
  const isMobile = useIsMobile();
  const [desktopSheet, setDesktopSheet] = useState(false);

  // Durable dismiss (bug-315): hide the card optimistically (keyed on conversationId, matching the
  // section filters) AND persist a reject_relevance audit when the item carries a briefingItemId, so
  // the today route's filterActionedItems keeps it gone on reload. No briefingItemId (legacy
  // briefings) → local-only hide, same as before.
  const dismissItem = (conversationId: string, briefingItemId?: string) => {
    setDismissed(prev => new Set(prev).add(conversationId));
    if (briefingItemId) audit.mutate({ itemId: briefingItemId, action: 'reject_relevance' });
  };

  useEffect(() => {
    if (shouldMarkViewed(readOnly, data.status, data.viewed_at) && !viewedRef.current) {
      viewedRef.current = true;
      apiFetch('/api/surfaces/team/today/viewed', { method: 'POST' })
        .then(() => queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-today'] }))
        .catch(() => {});
    }
  }, [data.viewed_at, data.status, queryClient, readOnly]);

  const greetingDate =
    data.status === 'no_team'
      ? 'No team yet'
      : data.status === 'not_generated'
        ? 'Pending'
        : 'Today';

  if (data.status === 'no_team') {
    return (
      <EmptyShell date={greetingDate} lede="Create or join a team to start seeing daily briefings.">
        <button className="btn btn-primary sm" onClick={() => onNav('settings')}>
          Settings <span className="arr">→</span>
        </button>
      </EmptyShell>
    );
  }
  if (data.status === 'not_generated') {
    // bug-418: "pending" is where Regenerate matters most — a meetings-only day before the 3 AM
    // sweep lands here, and forcing generation produces the brief now instead of waiting.
    return (
      <EmptyShell date={greetingDate} lede="Today's briefing hasn't been generated yet. Check back soon.">
        <span style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {!readOnly && <RegenerateButton className="btn btn-secondary sm" />}
          <button className="btn btn-secondary sm" onClick={() => onNav('briefing-history')}>
            Past briefings <span className="arr">→</span>
          </button>
        </span>
      </EmptyShell>
    );
  }
  if (data.status === 'empty' || !data.data) {
    // bug-418: the quiet-day state now keeps the ONLY path to past briefings (a nav-less
    // route) and surfaces recent briefings inline via the shared QuietDayState, instead of
    // the old dead-end "Quiet day + Sources" shell. Sources stays as the inline CTA; the
    // mobile connect-on-desktop nudge moves to the footer slot (still !readOnly).
    return (
      <QuietDayState
        onNav={onNav}
        readOnly={readOnly}
        footer={
          isMobile && !readOnly ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start', marginTop: 16 }}>
              <p style={{ fontSize: 14, color: 'var(--text2)', margin: 0 }}>
                Connect more of your tools to get richer briefings.
              </p>
              <button className="v2-btn" onClick={() => setDesktopSheet(true)}
                style={{ background: 'var(--accent)', color: '#fff', border: 'none' }}>
                Connect on your computer
              </button>
              <ConnectOnDesktopSheet open={desktopSheet} onClose={() => setDesktopSheet(false)} />
            </div>
          ) : undefined
        }
      >
        <button className="btn btn-secondary sm" onClick={() => onNav('sources')}>
          Sources <span className="arr">→</span>
        </button>
      </QuietDayState>
    );
  }

  const { summaryStats, whatChanged, needsYourInput, knowledgeCaptured, relevantToYourWork, lead } = data.data;
  // figure b / figure e — optional (legacy briefings omit them); read defensively.
  const sharedDecisions = data.data.sharedDecisions ?? [];
  const projectProgress = data.data.projectProgress ?? [];

  const needs = needsYourInput.filter(n => !dismissed.has(n.conversationId));
  const relevant = (relevantToYourWork ?? []).filter(
    r => ['high', 'medium'].includes(r.confidenceBand) && !dismissed.has(r.conversationId),
  );

  const tod = timeOfDay();
  const name = firstName(userName);

  return (
    <div className="v2-content ov-surface">
      <div className="wrap">
        {/* ---- Masthead ---- */}
        <div className="shead">
          <div>
            <div className="crumb">
              {formatCrumb(data.generated_at)}
              {data.is_previous && ' · yesterday'}
            </div>
            <h1 className="greeting">
              Good {tod}{name ? `, ${name}` : ''}. Your team is <span className="em">in sync</span>.
            </h1>
          </div>
          {/* Live view: a forward link into the history list. Historical (readOnly)
              detail view: a back affordance to the list it was opened from. */}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onNav('briefing-history')}
          >
            {readOnly ? (
              <>
                <span className="arr">←</span> Past briefings
              </>
            ) : (
              <>
                Past briefings <span className="arr">→</span>
              </>
            )}
          </button>
        </div>
        {lead && <p className="sub">{lead}</p>}

        <div className="statstrip">
          {summaryStats.map((s, i) => (
            <span className="stat" key={`${s.label}-${i}`}>
              <span className={`dot ${statDot(s.tone)}`} />
              {s.value} {s.label}
            </span>
          ))}
        </div>

        {/* ---- NEEDS YOUR INPUT ---- */}
        {needs.length > 0 && (
          <div className="sec-block">
            <div className="sec-label">
              <span className="kick rose"><span className="d" />NEEDS YOUR INPUT</span>
              <span className="ct">{needs.length}</span>
            </div>
            {needs.map((item, i) => (
              <div
                key={item.conversationId || `nyi-${i}`}
                className="icard"
                style={{ borderColor: ROSE_BORDER, marginTop: i > 0 ? 8 : 0 }}
              >
                <div className="irow1">
                  <span className="iid mono">{shortId('cf', item.conversationId)}</span>
                  <span className="ititle">{item.title}</span>
                </div>
                {/* figure a: real branch divergence + conflict, only when the collision carries
                    GitHub enrichment (absent until GitHub is connected). */}
                {item.severity === 'collision' && item.branchDivergence && (
                  <BranchDivergenceDetail div={item.branchDivergence} />
                )}
                <div className="imeta">
                  {item.tag && <span className="lab">{item.tag}</span>}
                  {item.severity && (
                    <span className="lab" style={{ color: 'var(--rose)', borderColor: ROSE_LAB_BORDER }}>
                      {severityLabel(item.severity)}
                    </span>
                  )}
                  {[...new Set(item.sharedEntities ?? [])].map(e => (
                    <span className="lab" key={e}>{e}</span>
                  ))}
                  {item.authors && item.authors.length > 0 && (
                    <span className="stat">
                      {item.authors.slice(0, 2).map((a, ai) => (
                        <span
                          key={`${a}-${ai}`}
                          className={`av sq s16 ${avColor(a)}`}
                          style={ai > 0 ? { marginLeft: -6 } : undefined}
                        >
                          {initials(a)}
                        </span>
                      ))}
                      &nbsp;{item.authors.slice(0, 2).join(' & ')}
                    </span>
                  )}
                  {item.timestamp && (
                    <span className="mono" style={{ color: 'var(--faint)', fontSize: '10.5px' }}>
                      {relativeTime(item.timestamp)}
                    </span>
                  )}
                  <span className="asg">
                    <span className="actions">
                      <button className="btn btn-primary sm" onClick={() => onNav('conversations/' + item.conversationId)}>
                        Resolve <span className="arr">→</span>
                      </button>
                      <button
                        className="btn btn-secondary sm"
                        onClick={() => dismissItem(item.conversationId, item.briefingItemId)}
                      >
                        Dismiss
                      </button>
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ---- RELEVANT TO YOUR WORK ---- */}
        {relevant.length > 0 && (
          <div className="sec-block">
            <div className="sec-label">
              <span className="kick acc"><span className="d" />RELEVANT TO YOUR WORK</span>
              <span className="ct">{relevant.length}</span>
            </div>
            <div className="mlist">
              {relevant.map((r, i) => (
                <div key={r.conversationId || `rel-${i}`} className="mrow">
                  <span className={`av sq s24 ${avColor(r.authorName)}`}>{initials(r.authorName)}</span>
                  <div className="mbody">
                    <div className="mtitle">
                      <span className="hi">{r.topic}</span>
                    </div>
                    <div className="mmeta">
                      <span className="mpill">
                        <span className={`dot ${r.confidenceBand === 'high' ? 'acc' : 'amb'}`} />
                        {r.confidenceBand === 'high' ? 'High relevance' : 'Medium'}
                      </span>
                      <span>{r.site ? `${siteLabel(r.site)} · ${r.authorName}` : r.authorName}</span>
                      {r.timestamp && <span className="mtime">{relativeTime(r.timestamp)}</span>}
                    </div>
                  </div>
                  <div className="mright">
                    {r.confidenceBand === 'high' && <span className="topmatch">Top match</span>}
                    <button className="btn btn-secondary sm" onClick={() => onNav('conversations/' + r.conversationId)}>
                      Open <span className="arr">→</span>
                    </button>
                    <button
                      className="btn btn-secondary sm"
                      onClick={() => dismissItem(r.conversationId, r.briefingItemId)}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- DECISIONS FORMING (figure b: opinion-lean meter) ---- */}
        {sharedDecisions.length > 0 && (
          <div className="sec-block">
            <div className="sec-label">
              <span className="kick acc"><span className="d" />DECISIONS FORMING</span>
              <span className="ct">{sharedDecisions.length}</span>
            </div>
            <div className="mlist">
              {sharedDecisions.map((decision: SharedDecision, di: number) => {
                // Backend already excludes no_signal; filter defensively so no marker is placed for one.
                // `?? []`: the BFF returns the stored briefing JSON verbatim (no Zod re-parse), so the
                // contract's stances.default([]) is NOT applied at runtime — guard against a missing array.
                const placed = (decision.stances ?? []).filter(
                  (s): s is TeammateStance & { lean: Exclude<StanceLean, 'no_signal'> } => s.lean !== 'no_signal',
                );
                const entities = [...new Set(decision.sharedEntities ?? [])];
                return (
                  <div className="icard" key={decision.conversationId || `dec-${di}`}>
                    <div className="irow1">
                      <span className="kick acc"><span className="d" />Decision forming</span>
                      {decision.teamProjectName && <span className="lab">{decision.teamProjectName}</span>}
                    </div>
                    <div className="ititle">{decision.decisionQuestion}</div>
                    <div className="lean">
                      <div className="lean-track">
                        {placed.map((s, si) => (
                          <span
                            className="lean-mk"
                            style={{ left: `${LEAN_PERCENT[s.lean]}%` }}
                            key={s.conversationId || `mk-${di}-${si}`}
                            title={`${s.memberName} — ${s.positionSummary}`}
                          >
                            <span className={`av sq s20 ${avColor(s.memberName)}`}>
                              {s.initial || initials(s.memberName)}
                            </span>
                          </span>
                        ))}
                      </div>
                      <div className="lean-ends">
                        <span>{decision.poleFor}</span>
                        <span className="r">{decision.poleAgainst}</span>
                      </div>
                    </div>
                    {entities.length > 0 && (
                      <div className="imeta">
                        {entities.map(e => <span className="lab" key={e}>{e}</span>)}
                      </div>
                    )}
                    <ul className="stance-list">
                      {placed.map((s, si) => (
                        <li key={s.conversationId || `st-${di}-${si}`}>
                          <b>{s.memberName}</b> — {s.positionSummary}
                          {s.supportingQuote ? <> <span className="sq-quote">“{s.supportingQuote}”</span></> : null}
                          <span className="sconf">{s.confidenceBand} confidence</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- PROJECT PROGRESS (figure e: progress bar + milestones + ETA cadence) ---- */}
        {projectProgress.length > 0 && (
          <div className="sec-block">
            <div className="sec-label">
              <span className="kick amb"><span className="d" />PROJECT PROGRESS</span>
              <span className="ct">{projectProgress.length}</span>
            </div>
            <div className="mlist">
              {projectProgress.map((proj: ProjectProgress, pi: number) => {
                const pct = Math.max(0, Math.min(100, proj.progressPct ?? 0));
                const pctLabel = proj.pctIsEstimated ? `~${pct}% (estimated)` : `${pct}%`;
                const blockers = [...new Set(proj.blockers ?? [])];
                return (
                  <div className="icard" key={proj.projectId || `prog-${pi}`}>
                    <div className="irow1">
                      <span className="kick amb"><span className="d" />In progress</span>
                      <span className="ititle">{proj.projectName}</span>
                      <span className="prog-pct">{pctLabel}</span>
                    </div>
                    <div className="bar"><i style={{ width: `${pct}%` }} /></div>
                    {proj.milestoneNarrative && <div className="fig-desc">{proj.milestoneNarrative}</div>}
                    {(proj.lastShippedMilestone || proj.currentMilestone) && (
                      <div className="prog-ms">
                        {proj.lastShippedMilestone && <span className="ms done">{proj.lastShippedMilestone}</span>}
                        {proj.currentMilestone && <span className="ms cur">{proj.currentMilestone}</span>}
                      </div>
                    )}
                    {blockers.length > 0 && (
                      <div className="imeta">
                        <span className="lab" style={{ color: 'var(--rose)', borderColor: ROSE_LAB_BORDER }}>Blockers</span>
                        {blockers.map(b => <span className="lab" key={b}>{b}</span>)}
                      </div>
                    )}
                    <div className="prog-eta">
                      {/* ETA is a cadence label ("active this week"), never a date. */}
                      {proj.etaLabel && <span>{proj.etaLabel}</span>}
                      <span className="mpill"><span className="dot acc" />{proj.confidenceBand} confidence</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- WHAT CHANGED ---- */}
        {whatChanged.length > 0 && (
          <div className="sec-block">
            <div className="sec-label">
              <span className="kick grn"><span className="d" />WHAT CHANGED</span>
              <span className="ct">{whatChanged.length}</span>
            </div>
            <div className="mlist">
              {whatChanged.map((item, i) => {
                // Contributors to this change: the grouped author union, or the single
                // author for an ungrouped row / legacy briefing without `people`.
                const people = item.people && item.people.length > 0 ? item.people : [item.memberName];
                const shown = people.slice(0, 2);
                const extra = people.length - shown.length;
                return (
                  <div
                    key={item.conversationId || `wc-${i}`}
                    className="mrow"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onNav('conversations/' + item.conversationId)}
                  >
                    <span className={`dot ${catDot(item.category)}`} />
                    <div className="mbody">
                      <div className="mtitle">
                        {catLabel(item.category)} · <b style={{ color: 'var(--inkB)' }}>{item.headline || item.text || item.topic}</b>
                        {item.why ? ` ${item.why}` : ''}
                      </div>
                      <div className="mmeta">
                        <span className="avstack">
                          {shown.map((p, pi) => (
                            <span
                              key={`${p}-${pi}`}
                              className={`av sq s16 ${avColor(p)}`}
                              style={pi > 0 ? { marginLeft: -5 } : undefined}
                            >
                              {initials(p)}
                            </span>
                          ))}
                          {extra > 0 && (
                            <span className="av sq s16 more" style={{ marginLeft: -5 }}>+{extra}</span>
                          )}
                        </span>
                        <span>{shown.join(' & ')}{extra > 0 ? ` +${extra}` : ''}</span>
                        {item.changeType && <span className="lab">{item.changeType}</span>}
                        <span className="mtime">{relativeTime(item.timestamp)}</span>
                      </div>
                      {/* figure c: real diffstat (+X −Y / files / branch→base), only when the item
                          carries GitHub enrichment (absent until GitHub is connected). */}
                      {item.diffstat && <DiffstatRow diffstat={item.diffstat} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- KNOWLEDGE CAPTURED TODAY ---- */}
        {knowledgeCaptured.length > 0 && (
          <div className="sec-block">
            <div className="sec-label">
              <span className="kick cyan"><span className="d" />KNOWLEDGE CAPTURED TODAY</span>
              <span className="ct">{knowledgeCaptured.length}</span>
            </div>
            <div className="mlist">
              {knowledgeCaptured.map((k, i) => (
                <div
                  key={k.conversationId || `kc-${i}`}
                  className="cv-row"
                  onClick={() => onNav('conversations/' + k.conversationId)}
                >
                  <span className="iid mono">{shortId('cv', k.conversationId)}</span>
                  {k.site && <SourceIcon id={k.site} size={22} />}
                  <span className="ititle">{k.text}</span>
                  <span className="cv-meta">
                    {k.authorName && (
                      <span className={`av sq s16 ${avColor(k.authorName)}`}>{initials(k.authorName)}</span>
                    )}
                    {k.timestamp && (
                      <span className="mono" style={{ fontSize: '10px', color: 'var(--faint)' }}>
                        {relativeTime(k.timestamp)}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
            <div className="section-note" style={{ cursor: 'pointer' }} onClick={() => onNav('map')}>
              All {knowledgeCaptured.length} live on the Map — the briefing is just the slice that matters to you today.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
